import React, { useRef, useCallback, useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from 'react-router-dom';
import useAnalytics from '../hooks/useAnalytics';
import { Helmet } from 'react-helmet-async';

// Lazy load the video player
const VideoPlayer = React.lazy(() => import('./VideoPlayer'));

// Schema.org structured data
const schemaData = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Strive",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web-based",
  "description": "AI powered automation platform that streamlines product and sales operations. Reduces costs by 75% while improving quality and compliance.",
  "offers": {
    "@type": "Offer",
    "availability": "https://schema.org/OnlineOnly",
    "price": "499",
    "priceCurrency": "USD",
    "priceSpecification": {
      "@type": "PriceSpecification",
      "price": "499",
      "priceCurrency": "USD"
    }
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "ratingCount": "150"
  },
  "featureList": [
    "AI-powered automation",
    "QA automation",
    "Compliance monitoring",
    "Real-time analytics",
    "Product operations optimization"
  ],
  "provider": {
    "@type": "Organization",
    "name": "Strive",
    "url": "https://strive.us",
    "sameAs": [
      "https://www.linkedin.com/company/strive",
      "https://twitter.com/strive"
    ]
  },
  "video": {
    "@type": "VideoObject",
    "name": "Strive Platform Overview",
    "description": "Overview of Strive's AI-powered product operations platform",
    "thumbnailUrl": "https://customer-f5xkmr6krvr9p6pu.cloudflarestream.com/b922fd8fd0f091d8f616969e79def195/thumbnails/thumbnail.jpg",
    "uploadDate": "2024-01-01"
  }
};

// Separate secondary content for deferred render
const SecondaryContent = React.memo(() => (
  <>
    <h2 className="text-xl md:text-2xl text-gray-700 mb-8 max-w-2xl mx-auto">
      Strive transforms scattered product data into one clear source of truth. Make every decision with complete confidence.
    </h2>
  </>
));

SecondaryContent.displayName = 'SecondaryContent';

// Main component with optimized render
const Hero = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const analytics = useAnalytics();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleCalendarRequest = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    analytics.trackFormSubmission({
      formType: 'demo',
      formLocation: 'hero-section',
      formSuccess: true
    });
    navigate('/contact');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [analytics, navigate]);

  return (
    <>
      <Helmet>
        <title>Strive | AI Powered Product Operations Platform</title>
        <meta 
          name="description" 
          content="Transform your product operations with Strive's AI powered platform. Automate QA, enhance compliance monitoring, and reduce operational costs by 75%. Book a demo today."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* Preload video thumbnail for better LCP */}
        <link 
          rel="preload" 
          as="image" 
          href="https://customer-f5xkmr6krvr9p6pu.cloudflarestream.com/b922fd8fd0f091d8f616969e79def195/thumbnails/thumbnail.jpg"
          fetchPriority="high"
        />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      <section 
        id="hero"
        ref={heroRef}
        aria-labelledby="hero-heading"
        className="relative min-h-[100dvh] flex flex-col items-center justify-center px-4 sm:px-6 md:px-10 pt-32 sm:pt-40 md:pt-48 pb-8 sm:pb-12 md:pb-16 overflow-hidden"
      >
        <div className="relative max-w-7xl mx-auto w-full">
          {/* Priority content - render immediately */}
          <div className="text-center max-w-3xl mx-auto mb-6 sm:mb-8 md:mb-12">
            <HeroHeading />
            <React.Suspense fallback={null}>
              <SecondaryContent />
            </React.Suspense>
          </div>
          
          {/* Background - deferred */}
          <div 
            aria-hidden="true"
            className="fixed inset-0 bg-gradient-to-b from-blue-50 to-white pointer-events-none -z-10" 
          />
          
          {/* Video - deferred with loading priority */}
          <div className="relative max-w-[1024px] mx-auto mb-8 sm:mb-12 md:mb-16">
            <React.Suspense fallback={
              <div 
                className="rounded-xl sm:rounded-2xl overflow-hidden shadow-lg sm:shadow-2xl border border-white/50 bg-gray-100 animate-pulse" 
                style={{ paddingTop: '49.76958525345622%' }}
                aria-label="Loading video player..."
              />
            }>
              <VideoPlayer
                videoId="b922fd8fd0f091d8f616969e79def195"
                thumbnailUrl="https://customer-f5xkmr6krvr9p6pu.cloudflarestream.com/b922fd8fd0f091d8f616969e79def195/thumbnails/thumbnail.jpg"
                title="Strive Platform Overview"
                aria-label="Watch Strive platform overview video"
                loading={isMobile ? "lazy" : "eager"}
              />
            </React.Suspense>
          </div>

          {/* CTA - deferred */}
          <div className="text-center">
            <Link 
              to="/contact"
              aria-label="Schedule a demo of the Strive platform"
            >
              <Button 
                className="px-8 sm:px-16 py-6 sm:py-8 text-xl sm:text-2xl font-medium bg-primary text-white rounded-xl sm:rounded-2xl shadow-lg hover:bg-primary/90 hover:shadow-xl hover:translate-y-[-2px] active:translate-y-0 transition-all duration-300 ease-in-out border-0 touch-manipulation"
                onClick={handleCalendarRequest}
              >
                Book a demo
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

// Update the HeroHeading component
const HeroHeading = () => (
  <h1 
    id="hero-heading"
    className="hero-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
  >
    Back your roadmap with data everyone trusts.
  </h1>
);

export default Hero;
